// ---------------------------------------------------------------------------
// The phone keyboard, wired to the answer builder rather than to a text field.
//
// On a phone there is no keyboard until something editable is focused, so the
// word box holds an invisible input stretched over it: tapping the box taps the
// input, the OS puts its keyboard up, and every keystroke is routed into the
// same `typeLetter` the tiles go through. That is the whole point. A letter the
// player does not hold is refused here exactly as it is on a desktop, because
// the refusal lives in the builder and this is only a source of keystrokes.
//
// THE VALUE OF THIS FIELD IS NEVER THE ANSWER. It is emptied on every path, and
// nothing reads it except the fallback below. A field whose value became the
// word would accept letters that are not on the board, which is the one thing
// the answer model exists to prevent.
//
// Three things about mobile keyboards that this is built around:
//
//   1. `keydown` is not reliable. Android's predictive keyboards report
//      `keyCode 229` / `key: 'Unidentified'` for ordinary letters, so the
//      letters are taken from `beforeinput`, which carries the text as data and
//      fires for every insertion including a predicted whole word. Backspace
//      IS reliable on keydown and is taken there, because an empty field does
//      not always produce a `deleteContentBackward` to catch instead — and a
//      backspace that does nothing at the start of a word would look broken.
//   2. `beforeinput` and `input` are one insertion seen twice. Preventing the
//      first stops the second from firing at all, so the ordinary path routes
//      once; `taken` is the belt to that braces, for an engine that fires
//      `input` anyway. Without it a keyboard that ignores the prevention would
//      type every letter twice.
//   3. Autocorrect, autocapitalise, spellcheck and predictive text are all off.
//      Left on, the OS inserts whole words and replaces what has been typed,
//      and a replacement is not something an answer built from nine tiles can
//      take. The 16px type size is not a style either: iOS zooms the page when
//      a focused field is smaller than that, keyboard and board together.
//
// It renders only on a phone. A desktop player types with nothing focused and
// must keep doing so — see the window listener in LettersPlayScreen — and a
// field that had to be clicked first would be a regression there.
// ---------------------------------------------------------------------------

import { useEffect, useRef } from 'react';
import type { KeyboardEvent, ReactElement, RefObject } from 'react';

export interface AnswerKeyboardFieldProps {
  /** The field itself, so the screen can focus and blur it. */
  fieldRef: RefObject<HTMLInputElement | null>;
  /** One character at a time, into the answer builder, which may refuse it. */
  onLetter: (letter: string) => void;
  onBackspace: () => void;
  onDeleteForward: () => void;
  onMoveCursor: (delta: number) => void;
  /** Enter, and the OS keyboard's own action key, which are the same press. */
  onEnter: () => void;
  /** Focus is what the layout reads to know a keyboard is probably up. */
  onFocusChange: (focused: boolean) => void;
  /** What the OS draws on its action key. */
  enterKeyHint: 'send' | 'done';
  /** The field has no visible label of its own; the box around it is the label. */
  label: string;
}

/**
 * Every insertion type that means "put these characters in".
 *
 * `insertCompositionText` is deliberately not one of them: it cannot be
 * prevented, and it reports the whole string composed so far on every keystroke,
 * so routing it would type CCACAT for "cat". A composition is routed once, when
 * it ends.
 */
const INSERTIONS = new Set([
  'insertText',
  'insertFromPaste',
  'insertFromDrop',
  'insertReplacementText',
  'insertFromYank',
]);

export function AnswerKeyboardField({
  fieldRef,
  onLetter,
  onBackspace,
  onDeleteForward,
  onMoveCursor,
  onEnter,
  onFocusChange,
  enterKeyHint,
  label,
}: AnswerKeyboardFieldProps): ReactElement {
  // `beforeinput` is subscribed natively rather than through React, because
  // React's `onBeforeInput` has been a synthesised event rather than the DOM one
  // for most of its life and the `inputType` is the whole of what this reads.
  // The handlers reach it through a ref so the subscription is set up once and
  // does not resubscribe on every clock frame.
  const latest = useRef({ onLetter, onBackspace, onDeleteForward, onEnter });
  useEffect(() => {
    latest.current = { onLetter, onBackspace, onDeleteForward, onEnter };
  }, [onLetter, onBackspace, onDeleteForward, onEnter]);

  /** The last insertion was routed and prevented, so `input` has nothing to do. */
  const taken = useRef(false);
  /** An IME is mid-composition, where every keystroke reports the whole string. */
  const composing = useRef(false);

  useEffect(() => {
    const field = fieldRef.current;
    if (field === null) return;

    const onBeforeInput = (event: Event) => {
      const change = event as InputEvent;
      const kind = change.inputType;
      if (kind === 'insertCompositionText') {
        // Nothing to take: the IME owns this until `compositionend`.
        taken.current = false;
        return;
      }
      // Set on every insertion, so a stale `true` from a prevented one cannot
      // swallow the next.
      taken.current = true;
      event.preventDefault();

      if (INSERTIONS.has(kind)) {
        for (const character of change.data ?? '') latest.current.onLetter(character);
        return;
      }
      if (kind === 'insertLineBreak' || kind === 'insertParagraph') {
        latest.current.onEnter();
        return;
      }
      if (kind === 'deleteContentForward') {
        latest.current.onDeleteForward();
        return;
      }
      if (kind.startsWith('delete')) {
        // Every backward delete is one letter. A keyboard that offers
        // "delete word" on an empty field is deleting nothing of its own, and
        // taking the whole word away on it would be a surprise mid-round.
        latest.current.onBackspace();
        return;
      }
      // Anything else — a format command, an undo — is refused rather than
      // allowed to put something in a value nothing reads.
    };

    field.addEventListener('beforeinput', onBeforeInput);
    return () => field.removeEventListener('beforeinput', onBeforeInput);
  }, [fieldRef]);

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    // Backspace explicitly, and before anything can decide there is nothing to
    // delete: the field is always empty, so a keyboard that only reports a
    // deletion through `beforeinput` would report nothing at all.
    if (event.key === 'Backspace') {
      event.preventDefault();
      onBackspace();
      return;
    }
    if (event.key === 'Delete') {
      event.preventDefault();
      onDeleteForward();
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      onMoveCursor(-1);
      return;
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      onMoveCursor(1);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      onEnter();
    }
  };

  return (
    <input
      ref={fieldRef}
      data-testid="answer-input"
      type="text"
      inputMode="text"
      enterKeyHint={enterKeyHint}
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck={false}
      // Password managers offer to fill anything that looks like a field, and an
      // overlay landing on the board mid-round is worse than most bugs.
      data-1p-ignore=""
      data-lpignore="true"
      aria-label={label}
      defaultValue=""
      onKeyDown={onKeyDown}
      onCompositionStart={() => {
        composing.current = true;
      }}
      onCompositionEnd={(event) => {
        composing.current = false;
        const composed = event.currentTarget.value;
        event.currentTarget.value = '';
        if (taken.current) {
          taken.current = false;
          return;
        }
        for (const character of composed) onLetter(character);
      }}
      onInput={(event) => {
        const field = event.currentTarget;
        // Mid-composition the value is the whole string so far, every keystroke.
        // It is routed once, when the composition ends.
        if (composing.current) return;
        const raw = field.value;
        field.value = '';
        if (taken.current) {
          taken.current = false;
          return;
        }
        for (const character of raw) onLetter(character);
      }}
      onFocus={() => onFocusChange(true)}
      onBlur={() => onFocusChange(false)}
      // Over the whole box, so the box is the tap target and no JavaScript has
      // to reach for focus — a programmatic `focus()` outside a real tap is
      // refused by iOS anyway. Invisible, and with no caret of its own: the box
      // draws the caret, in the place the letters actually land.
      className="absolute inset-0 z-10 h-full w-full cursor-text appearance-none border-0 bg-transparent p-0 text-[16px] text-transparent caret-transparent opacity-0 outline-none"
    />
  );
}
